
import { ModalityOptions } from '../BloodVisionContext';
import { UIComponents } from './types/bloodVision';
import type { ZipCodeInputRefHandle } from './ZipCodeInput';
import type { RefObject } from 'react';

const STICKY_HEADER_SCROLL_BUFFER = 16;

export const getStickyHeaderScrollOffset = () => {
  const headerClearance = 120; 
  return headerClearance + STICKY_HEADER_SCROLL_BUFFER;
};

export const scrollElementIntoViewWithHeaderOffset = (
  element: HTMLElement,
  behavior: ScrollBehavior = 'smooth',
) => {
  const offset = getStickyHeaderScrollOffset();
  const targetTop =
    element.getBoundingClientRect().top + window.scrollY - offset;

  window.scrollTo({
    top: Math.max(0, targetTop),
    behavior,
  });
};

export type MissingCheckoutStep = 'zip' | 'modality' | 'hsa' | null;

type IncompleteStepParams = {
  zipCode: string;
  isZipValid: boolean;
  selectedModality: ModalityOptions | null;
  isHsaFsaIncluded: boolean | null;
  uiComponents: UIComponents;
  isCampaignActive: boolean;
};

type ScrollRefsParams = {
  zipCodeInputRef?: RefObject<ZipCodeInputRefHandle | null> | null;
  modalitySectionRef?: RefObject<HTMLDivElement | null> | null;
  hsaSectionRef?: RefObject<HTMLDivElement | null> | null;
  showSelectionRequiredHint?: (step: 'modality' | 'hsa') => void;
};

export const focusZipCodeInput = (
  zipCodeInputRef?: ScrollRefsParams['zipCodeInputRef'],
) => {
  zipCodeInputRef?.current?.scrollIntoView();
  zipCodeInputRef?.current?.focus();
};

export const getFirstIncompleteCheckoutStep = ({
  zipCode,
  isZipValid,
  selectedModality,
  isHsaFsaIncluded,
  uiComponents,
  isCampaignActive,
}: IncompleteStepParams): MissingCheckoutStep => {
  const zipRequired = uiComponents.zip_section;
  const modalityRequired = uiComponents.testing_location_section;
  const hsaRequired = !isCampaignActive && uiComponents.hsa_fsa_section;

  if (zipRequired && (!zipCode || zipCode.length !== 5 || !isZipValid)) {
    return 'zip';
  }

  if (modalityRequired && selectedModality === null) {
    return 'modality';
  }

  if (hsaRequired && isHsaFsaIncluded === null) {
    return 'hsa';
  }

  return null;
};

export const scrollToCheckoutStep = (
  missingStep: MissingCheckoutStep,
  {
    zipCodeInputRef,
    modalitySectionRef,
    hsaSectionRef,
    showSelectionRequiredHint,
  }: ScrollRefsParams,
) => {
  if (missingStep === 'zip') {
    focusZipCodeInput(zipCodeInputRef);
    return;
  }

  if (missingStep === 'modality') {
    const element = modalitySectionRef?.current;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      showSelectionRequiredHint?.('modality');
    }
    return;
  }

  if (missingStep === 'hsa') {
    const element = hsaSectionRef?.current;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      showSelectionRequiredHint?.('hsa');
    }
  }
};

export const getCheckoutHint = (missingStep: MissingCheckoutStep) => {
  if (missingStep === 'zip') {
    return 'Please enter a valid US ZIP code to continue';
  }
  if (missingStep === 'modality') {
    return 'Please select a testing location to continue';
  }
  if (missingStep === 'hsa') {
    return 'Please select your HSA/FSA eligibility to continue';
  }
  return '';
};
